import { daysBetween } from '~/calculations/calendar'
import type { Sufficiency } from '~/calculations/weightTrends'
import type { NutritionContext, RulesInputSnapshot } from './inputs/snapshot'
import type { RulesThresholds } from './thresholds'

/**
 * Normalised observations, with no judgements in them.
 *
 * "Weight changed −0.42% this week" is a fact. "That is too fast" is a ruleset
 * conclusion. Keeping the split is what lets identical facts mean different
 * things in different phases (implementation-plan.md §4) — the same −0.42% is
 * on target during fat loss and off target during a lean bulk.
 */

/** Where a weekly rate sits relative to the phase's own configured range. */
export type RatePosition = 'insideRange' | 'fasterThanRange' | 'slowerThanRange'

/** Where a rate sits relative to a range, with no assumption about direction. */
export type RangeSide = 'above' | 'inside' | 'below'

export type WaistDirection = 'falling' | 'rising' | 'flat'

export interface WeightFacts {
  readonly currentSufficiency: Sufficiency
  readonly previousSufficiency: Sufficiency
  /** Present only when both windows were sufficient (D-036). */
  readonly weeklyChangePercent: number | undefined
  readonly weeklyChangeKg: number | undefined
  readonly currentAverageKg: number | undefined
  readonly entryCount: number
  /** True when the change is small enough to be indistinguishable from noise. */
  readonly isFlat: boolean | undefined
}

export interface WaistFacts {
  readonly available: boolean
  readonly latestCm: number | undefined
  readonly changeCm: number | undefined
  readonly direction: WaistDirection | undefined
  readonly daysBetweenSessions: number | undefined
  readonly latestQualityWarning: boolean
}

export interface PhaseFacts {
  readonly weekNumber: number
  readonly daysToPlannedEnd: number
  readonly atOrPastPlannedEnd: boolean
  readonly targetMinPercent: number
  readonly targetMaxPercent: number
  readonly ratePosition: RatePosition | undefined
  /**
   * Where the rate sits relative to the range, **without** the direction
   * heuristic.
   *
   * `ratePosition` answers "faster or slower than intended", which requires
   * knowing which way the phase is trying to go. That question is meaningless
   * for a range straddling zero: maintenance runs −0.25% to +0.25%, so "faster"
   * has no referent, and the sign heuristic silently reads the whole phase as a
   * losing one — which made a 0.6% *gain* classify as "slower than range".
   * Maintenance uses this instead.
   */
  readonly rangeSide: RangeSide | undefined
}

/**
 * Recovery, as observations rather than a verdict (D-066).
 *
 * The thresholds live in `rules/thresholds.ts` and are applied by the rulesets,
 * not here — this file states what was rated, and the families decide what that
 * means. Every item is phrased so 5 is the better outcome, which is what lets a
 * single threshold apply across all five.
 */
export interface RecoveryFacts {
  readonly available: boolean
  /** The worst of the five items this week. */
  readonly lowestItemScore: number | undefined
  readonly meanScore: number | undefined
  /**
   * How far the five-item mean sits **below** its trailing four-week average, as
   * a positive number. Absent until there is an average to compare against.
   */
  readonly meanDropFromFourWeekAverage: number | undefined
}

/**
 * Strength, as counts rather than a verdict (D-066, D-069).
 *
 * D-066's decline rule needs a size, a breadth and a duration. The size is
 * applied per lift when counting `liftsDecliningThisWeek`; the breadth and
 * duration are the two counts here, and the ruleset compares them against the
 * approved thresholds.
 */
export interface StrengthFacts {
  readonly liftsTracked: number
  /** Lifts whose estimate is at or below the decline threshold this week. */
  readonly liftsDecliningThisWeek: number
  /** Consecutive weeks, ending this one, meeting the breadth condition. */
  readonly consecutiveWeeksDeclining: number
  /** True when no lift produced a comparable figure at all. */
  readonly unavailable: boolean
}

export interface DerivedFacts {
  readonly weight: WeightFacts
  readonly waist: WaistFacts
  readonly phase: PhaseFacts | undefined
  readonly recovery: RecoveryFacts
  readonly strength: StrengthFacts
  /**
   * Carried through unchanged from the snapshot (D-096).
   *
   * Nothing is derived from it here on purpose: whether the configured rate
   * disagrees with the phase is a *judgement*, and judgements belong to the
   * rulesets. Deciding it here would put the conflict rule's threshold in the
   * observation layer, where no rule could be shown to read it.
   */
  readonly nutrition: NutritionContext
}

export function deriveFacts(
  snapshot: RulesInputSnapshot,
  thresholds: RulesThresholds,
): DerivedFacts {
  const weight = deriveWeightFacts(snapshot, thresholds)
  return {
    weight,
    waist: deriveWaistFacts(snapshot, thresholds),
    phase: derivePhaseFacts(snapshot, weight, thresholds),
    /* Carried through unchanged: both arrive already summarised, because both
       need history the engine is not allowed to read (D-046, and the layering
       guard). Nothing is judged here. */
    recovery: snapshot.recovery,
    strength: snapshot.strength,
    nutrition: snapshot.nutrition,
  }
}

function deriveWeightFacts(snapshot: RulesInputSnapshot, thresholds: RulesThresholds): WeightFacts {
  const { current, previous, comparison } = snapshot.weight
  const weeklyChangePercent = comparison?.percentChange as number | undefined

  return {
    currentSufficiency: current.sufficiency,
    previousSufficiency: previous.sufficiency,
    weeklyChangePercent,
    weeklyChangeKg: comparison?.changeKg as number | undefined,
    currentAverageKg: current.averageKg as number | undefined,
    entryCount: current.entryCount,
    isFlat:
      weeklyChangePercent === undefined
        ? undefined
        : Math.abs(weeklyChangePercent) < thresholds.flatWeightBandPercent,
  }
}

function deriveWaistFacts(snapshot: RulesInputSnapshot, thresholds: RulesThresholds): WaistFacts {
  const waist = snapshot.waist

  if (!waist) {
    return {
      available: false,
      latestCm: undefined,
      changeCm: undefined,
      direction: undefined,
      daysBetweenSessions: undefined,
      latestQualityWarning: false,
    }
  }

  const hasPrevious = waist.previousCm !== undefined && waist.previousDate !== undefined
  const changeCm = hasPrevious ? waist.latestCm - (waist.previousCm as number) : undefined

  return {
    available: true,
    latestCm: waist.latestCm,
    changeCm,
    direction: changeCm === undefined ? undefined : classifyWaist(changeCm, thresholds),
    daysBetweenSessions: hasPrevious
      ? daysBetween(waist.previousDate as never, waist.latestDate)
      : undefined,
    latestQualityWarning: waist.latestExceededSpreadThreshold,
  }
}

function classifyWaist(changeCm: number, thresholds: RulesThresholds): WaistDirection {
  if (changeCm <= -thresholds.waistFallMinCm) return 'falling'
  if (changeCm >= thresholds.waistFallMinCm) return 'rising'
  return 'flat'
}

function derivePhaseFacts(
  snapshot: RulesInputSnapshot,
  weight: WeightFacts,
  thresholds: RulesThresholds,
): PhaseFacts | undefined {
  const phase = snapshot.phase
  if (!phase) return undefined

  const daysToPlannedEnd = daysBetween(snapshot.reference, phase.plannedEndDate)

  return {
    weekNumber: phase.weekNumber,
    daysToPlannedEnd,
    atOrPastPlannedEnd: daysToPlannedEnd <= 0,
    targetMinPercent: phase.targetWeeklyChangeMinPercent,
    targetMaxPercent: phase.targetWeeklyChangeMaxPercent,
    ratePosition:
      weight.weeklyChangePercent === undefined
        ? undefined
        : positionAgainstRange(weight.weeklyChangePercent, phase, thresholds),
    rangeSide:
      weight.weeklyChangePercent === undefined
        ? undefined
        : sideOfRange(weight.weeklyChangePercent, phase, thresholds),
  }
}

/** Plain above/inside/below. Shares the epsilon so both agree on a boundary. */
function sideOfRange(
  changePercent: number,
  phase: { targetWeeklyChangeMinPercent: number; targetWeeklyChangeMaxPercent: number },
  thresholds: RulesThresholds,
): RangeSide {
  const epsilon = thresholds.rateComparisonEpsilonPercent
  const low = Math.min(phase.targetWeeklyChangeMinPercent, phase.targetWeeklyChangeMaxPercent)
  const high = Math.max(phase.targetWeeklyChangeMinPercent, phase.targetWeeklyChangeMaxPercent)

  if (changePercent < low - epsilon) return 'below'
  if (changePercent > high + epsilon) return 'above'
  return 'inside'
}

/**
 * Compare a signed weekly rate against the phase's configured signed range.
 *
 * "Faster" means further in the phase's intended direction than configured, so
 * for a fat-loss range of −0.7…−0.4, a change of −0.9 is faster and −0.2 is
 * slower. The epsilon makes a rate exactly at a boundary count as inside, which
 * matters because 0.4 has no exact binary representation.
 */
function positionAgainstRange(
  changePercent: number,
  phase: { targetWeeklyChangeMinPercent: number; targetWeeklyChangeMaxPercent: number },
  thresholds: RulesThresholds,
): RatePosition {
  const epsilon = thresholds.rateComparisonEpsilonPercent
  const low = Math.min(phase.targetWeeklyChangeMinPercent, phase.targetWeeklyChangeMaxPercent)
  const high = Math.max(phase.targetWeeklyChangeMinPercent, phase.targetWeeklyChangeMaxPercent)

  if (changePercent < low - epsilon)
    return signedFaster(low, high) ? 'fasterThanRange' : 'slowerThanRange'
  if (changePercent > high + epsilon)
    return signedFaster(low, high) ? 'slowerThanRange' : 'fasterThanRange'
  return 'insideRange'
}

/** True when moving below the range means moving faster — i.e. a losing phase. */
function signedFaster(low: number, high: number): boolean {
  return low < 0 || high <= 0
}
